import React, { useCallback, useEffect, useState } from "react";
import { API_URL } from "../../config";

export default function SsoTab({ token }) {
  const auth = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const [data, setData] = useState(null);
  const emptyForm = { name: "", uris: "", unverified: false, doctors: false, operator: "", details: "", purpose: "", privacy: "" };
  const [form, setForm] = useState(emptyForm);
  const [secret, setSecret] = useState(null);   // секрет показывается один раз
  const [msg, setMsg] = useState("");
  const [evFor, setEvFor] = useState(null);                 // клиент, у которого открыты настройки событий
  const [ev, setEv] = useState({ url: "", secret: "", year: "" });

  const load = useCallback(() => {
    fetch(`${API_URL}/admin/oidc/clients`, { headers: auth }).then(r => (r.ok ? r.json() : null)).then(setData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const call = async (path, method, body) => {
    setMsg("");
    const r = await fetch(`${API_URL}${path}`, { method, headers: auth, body: body ? JSON.stringify(body) : undefined });
    const out = await r.json().catch(() => ({}));
    if (!r.ok) { setMsg(typeof out.detail === "string" ? out.detail : "Ошибка запроса"); return null; }
    return out;
  };

  const create = async e => {
    e.preventDefault();
    const out = await call("/admin/oidc/clients", "POST", {
      name: form.name, redirect_uris: form.uris.split(/\s+/).filter(Boolean),
      issue_unverified_email: form.unverified, require_verified_doctor: form.doctors,
      operator_name: form.operator, operator_details: form.details, purpose: form.purpose, privacy_url: form.privacy,
    });
    if (out) { setSecret({ client_id: out.client_id, client_secret: out.client_secret }); setForm(emptyForm); load(); }
  };

  const openEvents = c => { setEvFor(c.id); setEv({ url: c.events.url || "", secret: "", year: c.events.event_year || "" }); setMsg(""); };
  const saveEvents = async (c, enabled) => {
    const body = { events_url: ev.url || null, events_enabled: enabled };
    if (ev.secret) body.events_secret = ev.secret;
    if (ev.year) body.event_year = Number(ev.year);
    const out = await call(`/admin/oidc/clients/${c.id}`, "PATCH", body);
    if (out) { setEv({ ...ev, secret: "" }); load(); }
  };

  if (!data) return <p>Загрузка…</p>;
  return (
    <div>
      <h3 className="admin-section-title">Единый вход (OpenID Connect)</h3>
      <p style={{ color: "#667", fontSize: "0.9rem", lineHeight: 1.5 }}>
        Сайты-партнёры входят через «Современный врач». Врач видит экран согласия, а партнёр получает только то, что врач разрешил.<br />
        Issuer: <code>{data.issuer}</code><br />Discovery: <code>{data.discovery}</code>
      </p>

      {secret && (
        <div className="card" style={{ background: "#fff8e6", border: "1px solid #f3d27a", marginBottom: 14 }}>
          <b>Секрет показан один раз.</b> Передайте партнёру защищённым каналом (не чатом и не репозиторием).<br />
          client_id: <code>{secret.client_id}</code><br />client_secret: <code>{secret.client_secret}</code>
          <div><button className="admin-btn" onClick={() => setSecret(null)} style={{ marginTop: 8 }}>Скрыть</button></div>
        </div>
      )}
      {msg && <p role="alert" style={{ color: "#b3261e" }}>{msg}</p>}

      <table className="admin-table">
        <thead><tr><th>Сайт</th><th>client_id</th><th>Адреса возврата</th><th>Условия</th><th></th></tr></thead>
        <tbody>
          {data.clients.length === 0 && <tr><td colSpan={5} style={{ color: "#888" }}>Пока нет подключённых сайтов.</td></tr>}
          {data.clients.map(c => (
            <tr key={c.id} style={c.is_active ? undefined : { opacity: 0.5 }}>
              <td>{c.name}{!c.is_active && " (отключён)"}</td>
              <td><code>{c.client_id}</code></td>
              <td>{c.redirect_uris.map(u => <div key={u}><code>{u}</code></div>)}</td>
              <td style={{ fontSize: "0.85rem" }}>
                {c.require_verified_doctor && <div>только подтверждённые врачи</div>}
                <div style={{ color: c.operator?.name ? "#555" : "#b3261e" }}>{c.operator?.name ? `оператор: ${c.operator?.name}` : "оператор ПДн не указан"}</div>
                {c.issue_unverified_email && <div>тест: выдаёт email_verified=false</div>}
              </td>
              <td style={{ whiteSpace: "nowrap" }}>
                <button className="admin-btn" onClick={async () => { if (window.confirm("Выпустить новый секрет? Старый перестанет работать.")) { const o = await call(`/admin/oidc/clients/${c.id}/rotate-secret`, "POST"); if (o) setSecret(o); } }}>Новый секрет</button>{" "}
                <button className="admin-btn" onClick={() => openEvents(c)}>События{c.events.enabled ? " ✓" : ""}</button>{" "}
                <button className="admin-btn" onClick={async () => { await call(`/admin/oidc/clients/${c.id}`, "PATCH", { is_active: !c.is_active }); load(); }}>{c.is_active ? "Отключить" : "Включить"}</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {data.clients.filter(c => c.id === evFor).map(c => (
        <div key={c.id} className="card" style={{ marginTop: 14 }}>
          <h4 style={{ marginTop: 0 }}>События для «{c.name}»</h4>
          <p style={{ color: "#667", fontSize: "0.88rem", lineHeight: 1.5, marginTop: 0 }}>
            Отправляются только о врачах, давших согласие этому сайту: вход, согласие, отзыв согласия. Без почты и имени. Подпись HMAC-SHA256.
            Включайте после того, как партнёр подтвердит, что приём событий открыт.
          </p>
          <div style={{ display: "grid", gap: 10, maxWidth: 640 }}>
            <input className="admin-input" placeholder="Адрес приёма событий (https://…)" value={ev.url} onChange={e => setEv({ ...ev, url: e.target.value })} />
            <input className="admin-input" type="password" autoComplete="off" placeholder={c.events.secret_set ? "Секрет подписи задан (введите, чтобы заменить)" : "Общий секрет подписи (не короче 16 символов)"} value={ev.secret} onChange={e => setEv({ ...ev, secret: e.target.value })} />
            <input className="admin-input" type="number" placeholder="Год мероприятия (необязательно)" value={ev.year} onChange={e => setEv({ ...ev, year: e.target.value })} />
            <div>
              <button className="admin-btn" onClick={() => saveEvents(c, c.events.enabled)}>Сохранить</button>{" "}
              <button className="admin-btn" onClick={() => saveEvents(c, !c.events.enabled)}>{c.events.enabled ? "Выключить отправку" : "Сохранить и включить отправку"}</button>
            </div>
            <div style={{ fontSize: "0.88rem", color: "#555" }}>
              Очередь: ожидают {c.queue.pending || 0} · отправлено {c.queue.sent || 0} · отклонено партнёром {c.queue.rejected || 0} · просрочено {c.queue.expired || 0}
            </div>
          </div>
        </div>
      ))}

      <h4 style={{ marginTop: 22 }}>Подключить сайт</h4>
      <form onSubmit={create} style={{ display: "grid", gap: 10, maxWidth: 640 }}>
        <input className="admin-input" placeholder="Название для экрана согласия *" value={form.name} required minLength={2} onChange={e => setForm({ ...form, name: e.target.value })} />
        <textarea className="admin-textarea" rows={3} required placeholder={"Адреса возврата (redirect_uri), по одному на строку.\nТолько https, без подстановок «*»."} value={form.uris} onChange={e => setForm({ ...form, uris: e.target.value })} />
        <input className="admin-input" placeholder="Оператор персональных данных сайта (название) — обязательно для рабочего клиента" value={form.operator} onChange={e => setForm({ ...form, operator: e.target.value })} />
        <input className="admin-input" placeholder="Реквизиты оператора: ИНН/ОГРН, адрес, контакт" value={form.details} onChange={e => setForm({ ...form, details: e.target.value })} />
        <input className="admin-input" placeholder="Цель обработки, например: регистрация участников конгресса" value={form.purpose} onChange={e => setForm({ ...form, purpose: e.target.value })} />
        <input className="admin-input" placeholder="Ссылка на политику конфиденциальности сайта (https) — обязательно для рабочего клиента" value={form.privacy} onChange={e => setForm({ ...form, privacy: e.target.value })} />
        <label><input type="checkbox" checked={form.doctors} onChange={e => setForm({ ...form, doctors: e.target.checked })} /> Пускать только подтверждённых врачей</label>
        <label><input type="checkbox" checked={form.unverified} onChange={e => setForm({ ...form, unverified: e.target.checked })} /> Тестовый режим: выдавать email_verified=false (не для рабочего клиента)</label>
        <div><button className="admin-btn" type="submit">Создать</button></div>
      </form>
    </div>
  );
}
